/**
 * Version Comparison page — side-by-side score tracking across answer revisions.
 *
 * Shows:
 * - Score summary cards for each version (with deltas highlighted)
 * - Score trend visualization (which dimensions improved/declined)
 * - Quick link to view each version's full evaluation
 *
 * The backend's /answers/{id}/compare endpoint returns all versions with
 * their latest evaluation scores, making this mostly a rendering exercise.
 */

import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { getAnswerComparison, VersionScoreSummary } from "../api/client";
import AppBreadcrumbs from "../components/AppBreadcrumbs";
import PageLoader from "../components/PageLoader";

// DIMENSIONS is a factory so its labels can be translated — call
// getDimensions(t) inside the component, which has a translation hook.
function getDimensions(t: TFunction) {
  return [
    { key: "situation_score", label: t("VersionComparison.dimensionSituation") },
    { key: "task_score", label: t("VersionComparison.dimensionTask") },
    { key: "action_score", label: t("VersionComparison.dimensionAction") },
    { key: "result_score", label: t("VersionComparison.dimensionResult") },
    { key: "engagement_score", label: t("VersionComparison.dimensionEngagement") },
    { key: "overall_score", label: t("VersionComparison.dimensionOverall") },
    { key: "average_score", label: t("VersionComparison.dimensionAverage") },
  ] as const;
}

/** Render a delta indicator: green up arrow, red down arrow, or dash */
function DeltaIndicator({ current, previous }: { current: number | null; previous: number | null }) {
  if (current === null || previous === null) return null;

  const delta = Number(current) - Number(previous);

  if (delta > 0) {
    return (
      <Chip
        icon={<ArrowUpwardIcon sx={{ fontSize: 14 }} />}
        label={`+${delta.toFixed(1)}`}
        size="small"
        color="success"
        variant="outlined"
        sx={{ ml: 0.5, height: 22, "& .MuiChip-label": { px: 0.5 } }}
      />
    );
  }

  if (delta < 0) {
    return (
      <Chip
        icon={<ArrowDownwardIcon sx={{ fontSize: 14 }} />}
        label={delta.toFixed(1)}
        size="small"
        color="error"
        variant="outlined"
        sx={{ ml: 0.5, height: 22, "& .MuiChip-label": { px: 0.5 } }}
      />
    );
  }

  return (
    <Chip
      icon={<RemoveIcon sx={{ fontSize: 14 }} />}
      label="0"
      size="small"
      variant="outlined"
      sx={{ ml: 0.5, height: 22, "& .MuiChip-label": { px: 0.5 } }}
    />
  );
}

/** Color a score cell based on 1-5 scale */
function scoreColor(score: number | null): string {
  if (score === null) return "text.secondary";
  const s = Number(score);
  if (s >= 4) return "success.main";
  if (s >= 3) return "warning.main";
  return "error.main";
}

export default function VersionComparison() {
  const { answerId } = useParams<{ answerId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation("pages-account");
  const DIMENSIONS = getDimensions(t);

  const {
    data: comparison,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["answer-comparison", answerId],
    queryFn: () => getAnswerComparison(answerId!),
    enabled: !!answerId,
  });

  if (isLoading) {
    return <PageLoader message={t("VersionComparison.loadingMessage")} />;
  }

  if (error || !comparison) {
    return (
      <Alert severity="error">
        {t("VersionComparison.loadFailedMessage")}
      </Alert>
    );
  }

  const versions = comparison.version_scores;
  const hasMultipleVersions = versions.length > 1;

  // Find best score per dimension for highlighting
  const bestScores: Record<string, number> = {};
  DIMENSIONS.forEach(({ key }) => {
    const scores = versions
      .map((v) => v[key as keyof VersionScoreSummary] as number | null)
      .filter((s): s is number => s !== null);
    if (scores.length > 0) {
      bestScores[key] = Math.max(...scores.map(Number));
    }
  });

  return (
    <Box sx={{ maxWidth: 960, mx: "auto" }}>
      <AppBreadcrumbs
        crumbs={[
          { label: t("VersionComparison.dashboardCrumb"), path: "/" },
          { label: t("VersionComparison.compareVersionsCrumb") },
        ]}
      />

      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
          >
            {t("VersionComparison.heading")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {comparison.target_role} &bull; {comparison.experience_level} &bull;{" "}
            {t("VersionComparison.versionsCount", { count: versions.length })}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          {t("VersionComparison.backButton")}
        </Button>
      </Stack>

      {/* Improvement Summary */}
      {hasMultipleVersions && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t("VersionComparison.improvementSummaryHeading")}
            </Typography>
            <ImprovementSummary versions={versions} />
          </CardContent>
        </Card>
      )}

      {/* Score Comparison Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t("VersionComparison.scoresByVersionHeading")}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>{t("VersionComparison.dimensionHeader")}</TableCell>
                  {versions.map((v) => (
                    <TableCell key={v.id} align="center" sx={{ fontWeight: 500 }}>
                      v{v.version_number}
                      {v.is_ai_assisted && (
                        <Chip
                          label={t("VersionComparison.aiChip")}
                          size="small"
                          sx={{ ml: 0.5, height: 18, fontSize: 10 }}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {DIMENSIONS.map(({ key, label }) => (
                  <TableRow key={key}>
                    <TableCell sx={{ fontWeight: 500 }}>{label}</TableCell>
                    {versions.map((v, idx) => {
                      const score = v[key as keyof VersionScoreSummary] as
                        | number
                        | null;
                      const prevScore =
                        idx > 0
                          ? (versions[idx - 1][
                              key as keyof VersionScoreSummary
                            ] as number | null)
                          : null;
                      const isBest =
                        score !== null && Number(score) === bestScores[key];

                      return (
                        <TableCell key={v.id} align="center">
                          <Stack
                            direction="row"
                            justifyContent="center"
                            alignItems="center"
                          >
                            <Typography
                              variant="body2"
                              fontWeight={isBest ? 500 : 400}
                              color={scoreColor(score)}
                            >
                              {score !== null ? Number(score).toFixed(key === "average_score" ? 1 : 0) : "—"}
                            </Typography>
                            {idx > 0 && (
                              <DeltaIndicator
                                current={score}
                                previous={prevScore}
                              />
                            )}
                          </Stack>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Version Cards */}
      <Typography variant="h6" gutterBottom>
        {t("VersionComparison.versionDetailsHeading")}
      </Typography>
      <Grid container spacing={2}>
        {versions.map((v) => (
          <Grid item xs={12} sm={6} key={v.id}>
            <Card
              sx={{
                height: "100%",
                border:
                  v.version_number === versions.length ? 2 : 0,
                borderColor: "secondary.main",
              }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="h6">
                    {t("VersionComparison.versionLabel", { number: v.version_number })}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    {v.version_number === versions.length && (
                      <Chip label={t("VersionComparison.latestChip")} size="small" color="success" />
                    )}
                    {v.is_ai_assisted && (
                      <Chip
                        label={t("VersionComparison.aiAssistedChip")}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Stack>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t("VersionComparison.wordCountLabel", { words: v.word_count ?? "?" })} &bull;{" "}
                  {new Date(v.created_at).toLocaleDateString()}
                </Typography>

                {v.average_score !== null ? (
                  <Typography
                    variant="h4"
                    fontWeight={500}
                    color={scoreColor(v.average_score)}
                    sx={{ my: 1 }}
                  >
                    {Number(v.average_score).toFixed(1)}
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                    >
                      {t("VersionComparison.outOfFiveSuffix")}
                    </Typography>
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ my: 1 }}
                  >
                    {v.evaluation_status === "queued" ||
                    v.evaluation_status === "analyzing"
                      ? t("VersionComparison.evaluatingLabel")
                      : t("VersionComparison.notEvaluatedLabel")}
                  </Typography>
                )}

                <Divider sx={{ my: 1 }} />

                {v.evaluation_id && (
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() =>
                      navigate(`/evaluations/${v.evaluation_id}`)
                    }
                  >
                    {t("VersionComparison.viewFullEvaluationButton")}
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

/**
 * Summary component showing the overall improvement trajectory.
 * Compares first version to latest version.
 */
function ImprovementSummary({
  versions,
}: {
  versions: VersionScoreSummary[];
}) {
  const { t } = useTranslation("pages-account");
  const first = versions[0];
  const latest = versions[versions.length - 1];

  if (!first || !latest) return null;
  if (first.average_score === null || latest.average_score === null) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("VersionComparison.noScoresYetMessage")}
      </Typography>
    );
  }

  const delta = Number(latest.average_score) - Number(first.average_score);
  const improved = delta > 0;
  const unchanged = delta === 0;

  return (
    <Stack direction="row" spacing={3} alignItems="center">
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="caption" color="text.secondary">
          {t("VersionComparison.v1AverageLabel")}
        </Typography>
        <Typography variant="h5" fontWeight={500}>
          {Number(first.average_score).toFixed(1)}
        </Typography>
      </Box>

      <Typography variant="h4" color="text.secondary">
        →
      </Typography>

      <Box sx={{ textAlign: "center" }}>
        <Typography variant="caption" color="text.secondary">
          {t("VersionComparison.versionAverageLabel", { number: latest.version_number })}
        </Typography>
        <Typography variant="h5" fontWeight={500}>
          {Number(latest.average_score).toFixed(1)}
        </Typography>
      </Box>

      <Chip
        label={
          unchanged
            ? t("VersionComparison.noChangeChip")
            : t("VersionComparison.pointsDeltaChip", { sign: improved ? "+" : "", delta: delta.toFixed(1) })
        }
        color={improved ? "success" : unchanged ? "default" : "error"}
        sx={{ fontWeight: 500, fontSize: 14, height: 32 }}
      />
    </Stack>
  );
}
